import type { LobKey } from "src/jsonForms/preScreen/types";
import { SEARCH_CONFIG_REGISTRY } from "./registry";

/**
 * The one module under src/claimsHistory/ that knows where a Search_Config
 * comes from, and the only one permitted to import from src/api/. When the
 * config moves to a backend operation, the implementation below is replaced
 * with one sending ResolveSearchConfigCommand and nothing else changes, so
 * keep validation, memoization, and metrics out of here.
 */
export interface SearchConfigSource {
  /** Resolves with an unvalidated document, or rejects. */
  load(lob: LobKey): Promise<unknown>;
}

/** Thrown when no Search_Config exists for a line of business. */
export class SearchConfigMissingError extends Error {
  constructor(readonly lob: LobKey) {
    super(`No claims history configuration registered for "${lob}"`);
    this.name = "SearchConfigMissingError";
  }
}

/**
 * Bundled implementation. Returns a promise so every caller is written
 * against the async contract from day one.
 *
 * The document is returned as `unknown` deliberately: it forces the resolver
 * to put even a bundled config through the validator, so the bundled path
 * exercises exactly the code the backend path will.
 */
export const bundledSearchConfigSource: SearchConfigSource = {
  async load(lob) {
    const entry = SEARCH_CONFIG_REGISTRY[lob];
    if (!entry) throw new SearchConfigMissingError(lob);
    return entry.searchConfig as unknown;
  },
};

export const searchConfigSource: SearchConfigSource = bundledSearchConfigSource;
